'use client';

/**
 * EventForwardTab — simplified forward tab for the event detail panel.
 *
 * Shows:
 * - Active mode label (Via Proxy / Via Browser) with a Configure link
 * - Proxy health indicator (green/red dot, polled every 5s, proxy mode only)
 * - Forward Event button
 * - Response/error display
 * - Config modal (rendered inline, opened via Configure link)
 */

import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FC,
} from 'react';

import {
  FORWARD_MODE_KEY,
  LEGACY_URL_KEY,
  PROXY_URL_KEY,
  TARGET_URL_KEY,
  getSampleServers,
  type ForwardMode,
  type SampleServer,
} from '@/constant/eventForwardSamples';
import { explorerAnalytics } from '@/lib/analytics';
import { pendoTrack } from '@/lib/pendo/trackPendoEvent';
import { copyToClipboard } from '@/lib/copyToClipboard';
import {
  forwardEvent,
  type ForwardError,
  type ForwardResult,
} from '@/lib/forwardEvent';
import { useEventExplorerStore } from '@/stores/useEventExplorerStore';
import type { ChargebeeEvent } from '@/types/events';
import { Button } from '@components/components/ui/button';
import { cn } from '@components/lib/utils';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Copy,
  Download,
  HelpCircle,
  Loader2,
  Settings,
  X,
  XCircle,
} from 'lucide-react';
import {
  EventForwardConfigModal,
  type ForwardConfig,
} from './EventForwardConfigModal';

// ─── Types ──────────────────────────────────────────────────────────────────

interface EventForwardTabProps {
  event: ChargebeeEvent;
}

// ─── Main Component ─────────────────────────────────────────────────────────

export const EventForwardTab: FC<EventForwardTabProps> = ({ event }) => {
  const { forwardConfig, setForwardConfig } = useEventExplorerStore();
  const { mode, proxyUrl, targetUrl } = forwardConfig;
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState<ForwardResult | null>(null);
  const [error, setError] = useState<ForwardError | null>(null);
  const [proxyConnected, setProxyConnected] = useState<boolean | null>(null);
  const [responseCopied, setResponseCopied] = useState(false);

  // Load config from localStorage on mount (with legacy migration)
  useEffect(() => {
    const savedMode = localStorage.getItem(
      FORWARD_MODE_KEY
    ) as ForwardMode | null;
    const savedProxy = localStorage.getItem(PROXY_URL_KEY);
    const savedTarget = localStorage.getItem(TARGET_URL_KEY);

    let migratedTarget = '';
    let resolvedProxy = '';

    if (!savedProxy && !savedTarget) {
      const legacy = localStorage.getItem(LEGACY_URL_KEY);
      if (legacy) {
        migratedTarget = legacy;
        localStorage.setItem(TARGET_URL_KEY, legacy);
        localStorage.removeItem(LEGACY_URL_KEY);
      }
    } else {
      resolvedProxy = savedProxy ?? '';
      migratedTarget = savedTarget ?? '';
    }

    setForwardConfig({
      mode:
        savedMode === 'proxy' || savedMode === 'direct' ? savedMode : 'proxy',
      proxyUrl: resolvedProxy,
      targetUrl: migratedTarget,
    });
  }, [setForwardConfig]);

  // Clear result/error when a different event is selected
  const eventId = event.id;
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional reset trigger
  useEffect(() => {
    setResult(null);
    setError(null);
  }, [eventId]);

  // Health check polling (proxy mode only, every 5s)
  useEffect(() => {
    if (mode !== 'proxy' || !proxyUrl) {
      setProxyConnected(null);
      return;
    }

    const checkHealth = async () => {
      try {
        const res = await fetch(`${proxyUrl.replace(/\/$/, '')}/health`, {
          method: 'GET',
        });
        setProxyConnected(res.ok);
      } catch {
        setProxyConnected(false);
      }
    };

    checkHealth();
    const intervalId = setInterval(checkHealth, 5000);
    return () => clearInterval(intervalId);
  }, [mode, proxyUrl]);

  // Forward the event using the configured mode
  const handleForward = useCallback(async () => {
    if (mode === 'proxy' && (!proxyUrl.trim() || !targetUrl.trim())) return;
    if (mode === 'direct' && !targetUrl.trim()) return;

    setIsSending(true);
    setResult(null);
    setError(null);
    setResponseCopied(false);

    const outcome = await forwardEvent(event, { mode, proxyUrl, targetUrl });
    if (outcome.result) setResult(outcome.result);
    if (outcome.error) setError(outcome.error);
    setIsSending(false);

    explorerAnalytics.eventForwarded({
      eventType: event.event_type || '',
      forwardMode: mode,
      isSuccess: outcome.result?.ok ?? false,
      statusCode: outcome.result?.status,
      durationMs: outcome.result?.durationMs,
    });
    pendoTrack('event_forwarded', {
      event_type: event.event_type || '',
      forward_mode: mode,
      is_success: outcome.result?.ok ?? false,
      status_code: outcome.result?.status,
      duration_ms: outcome.result?.durationMs,
    });
  }, [mode, proxyUrl, targetUrl, event]);

  // Copy response body to clipboard
  const handleCopyResponse = useCallback(async () => {
    if (!result?.body) return;
    try {
      await copyToClipboard(result.body);
      setResponseCopied(true);
      explorerAnalytics.eventForwardResponseCopied({
        responseSize: result.body?.length || 0,
      });
      setTimeout(() => setResponseCopied(false), 2000);
    } catch {
      // Clipboard not available
    }
  }, [result?.body]);

  // Save config from modal
  const handleConfigSave = useCallback(
    (config: ForwardConfig) => {
      setForwardConfig(config);

      localStorage.setItem(FORWARD_MODE_KEY, config.mode);
      localStorage.setItem(PROXY_URL_KEY, config.proxyUrl);
      localStorage.setItem(TARGET_URL_KEY, config.targetUrl);

      explorerAnalytics.eventForwardConfigured({
        forwardMode: config.mode,
      });
      pendoTrack('event_forward_configured', {
        forward_mode: config.mode,
      });

      setResult(null);
      setError(null);
    },
    [setForwardConfig]
  );

  const isConfigured =
    mode === 'proxy'
      ? proxyUrl.trim() !== '' && targetUrl.trim() !== ''
      : targetUrl.trim() !== '';

  return (
    <div className="p-4 space-y-3">
      {/* ── Configured: show help text + mode + health ── */}
      {isConfigured && (
        <>
          <p className="text-xs text-gray-500">
            Forward this event to a local server to test your webhook handler.
          </p>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Mode: {mode === 'proxy' ? 'Via Proxy' : 'Via Browser'}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setHelpModalOpen(true)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="Setup instructions"
              >
                <HelpCircle className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setConfigModalOpen(true)}
                className="flex items-center gap-1 text-xs text-shade-teal-600 hover:text-shade-teal-700 font-medium"
              >
                <Settings className="h-3.5 w-3.5" />
                Configure
              </button>
            </div>
          </div>
          {mode === 'proxy' && proxyUrl && (
            <HealthIndicator connected={proxyConnected} />
          )}
        </>
      )}

      {/* ── Not configured — detailed instructions ── */}
      {!isConfigured && (
        <EmptyStateInstructions onConfigure={() => setConfigModalOpen(true)} />
      )}

      {/* ── Endpoint details (read-only) + Forward button ── */}
      {isConfigured && (
        <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2">
          <p className="text-xs text-gray-500">
            {mode === 'proxy'
              ? 'Forwards via your proxy server to the target.'
              : 'Sends directly from browser. CORS required on target.'}
          </p>
          {mode === 'proxy' && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-gray-400 w-12 shrink-0">Proxy</span>
              <code
                className="flex-1 truncate font-mono text-xs text-gray-700"
                data-pendo-mask
              >
                {proxyUrl}
              </code>
            </div>
          )}
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-400 w-12 shrink-0">Target</span>
            <code
              className="flex-1 truncate font-mono text-xs text-gray-700"
              data-pendo-mask
            >
              {targetUrl}
            </code>
            <Button
              onClick={handleForward}
              disabled={isSending}
              size="sm"
              className="bg-shade-teal-600 text-white hover:bg-shade-teal-700 shrink-0"
            >
              {isSending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Forwarding…
                </>
              ) : (
                'Forward Event'
              )}
            </Button>
          </div>
        </div>
      )}

      {/* ── Compact download links (visible when configured, proxy mode) ── */}
      {isConfigured && mode === 'proxy' && <CompactDownloadLinks />}

      {/* ── Separator between config and results ── */}
      {(result || error) && <hr className="border-gray-200" />}

      {/* ── Response display ── */}
      {result && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-gray-500">
            Response from target server
          </p>
          <div
            className={cn(
              'rounded-md border p-3 text-sm',
              result.ok
                ? 'border-green-200 bg-green-50'
                : 'border-red-200 bg-red-50'
            )}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-medium">
                {result.ok ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-600" />
                )}
                <span className={result.ok ? 'text-green-700' : 'text-red-700'}>
                  {result.status} {result.statusText}
                </span>
                <span className="text-xs text-gray-500 font-normal">
                  ({result.durationMs}ms)
                </span>
              </div>
              {result.body && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCopyResponse}
                  className="h-7 w-7 text-gray-400 hover:text-gray-600"
                  title="Copy response"
                >
                  {responseCopied ? (
                    <Check className="h-3.5 w-3.5 text-green-500" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                </Button>
              )}
            </div>
            {result.body && (
              <pre
                className="mt-2 whitespace-pre-wrap break-all text-xs text-gray-600 max-h-64 overflow-y-auto font-mono"
                data-pendo-mask
              >
                {result.body}
              </pre>
            )}
          </div>
        </div>
      )}

      {/* ── Error display ── */}
      {error && (
        <div
          className={cn(
            'rounded-md border p-3 text-sm',
            error.isCorsLikely
              ? 'border-amber-200 bg-amber-50'
              : 'border-red-200 bg-red-50'
          )}
        >
          <div
            className={cn(
              'flex items-center gap-2 font-medium',
              error.isCorsLikely ? 'text-amber-700' : 'text-red-700'
            )}
          >
            {error.isCorsLikely ? (
              <AlertTriangle className="h-4 w-4" />
            ) : (
              <XCircle className="h-4 w-4" />
            )}
            {error.message}
          </div>
          {error.isCorsLikely && (
            <p className="mt-1 text-xs text-amber-600">
              {mode === 'proxy'
                ? 'Make sure your proxy server is running and includes CORS headers.'
                : 'Your server likely received the request, but the browser blocked the response. Add CORS headers or switch to Via Proxy mode.'}{' '}
              <button
                type="button"
                onClick={() => setConfigModalOpen(true)}
                className="underline font-medium"
              >
                Open configuration
              </button>
            </p>
          )}
        </div>
      )}

      {/* ── Config modal ── */}
      <EventForwardConfigModal
        open={configModalOpen}
        onOpenChange={setConfigModalOpen}
        onSave={handleConfigSave}
        initialConfig={{ mode, proxyUrl, targetUrl }}
      />

      {/* ── Help modal ── */}
      {helpModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="relative w-full max-w-lg mx-4 rounded-lg border border-gray-200 bg-white shadow-xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
              <h3 className="text-sm font-semibold text-gray-900">
                Send events to server
              </h3>
              <button
                type="button"
                onClick={() => setHelpModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-5 py-4">
              <EmptyStateInstructions
                showTitle={false}
                onConfigure={() => {
                  setHelpModalOpen(false);
                  setConfigModalOpen(true);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Health Indicator ───────────────────────────────────────────────────────

/** Shows a green/red/gray dot indicating proxy server connectivity. */
const HealthIndicator: FC<{ connected: boolean | null }> = ({ connected }) => (
  <div className="flex items-center gap-2 text-xs">
    {connected === null ? (
      <>
        <span className="h-2 w-2 rounded-full bg-gray-300" />
        <span className="text-gray-500">Checking proxy…</span>
      </>
    ) : connected ? (
      <>
        <span className="h-2 w-2 rounded-full bg-green-500" />
        <span className="text-green-600">Proxy connected</span>
      </>
    ) : (
      <>
        <span className="h-2 w-2 rounded-full bg-red-500" />
        <span className="text-red-600">Proxy not reachable</span>
      </>
    )}
  </div>
);

// ─── Shared Helpers ──────────────────────────────────────────────────────────

function downloadSample(sample: SampleServer) {
  const blob = new Blob([sample.code], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = sample.filename;
  a.click();
  URL.revokeObjectURL(url);
  explorerAnalytics.eventForwardSampleDownloaded({
    language: sample.language || '',
    filename: sample.filename || '',
  });
}

function useSamples() {
  return useMemo(
    () =>
      typeof window !== 'undefined'
        ? getSampleServers(window.location.origin)
        : [],
    []
  );
}

// ─── Flow Diagram ────────────────────────────────────────────────────────────

const FLOW_STEPS = [
  {
    label: 'Chargebee',
    icon: '⚡',
    color: 'bg-purple-50 border-purple-200 text-purple-700',
  },
  {
    label: 'Browser',
    icon: '🌐',
    color: 'bg-blue-50 border-blue-200 text-blue-700',
  },
  {
    label: 'Proxy Server',
    icon: '🔀',
    color: 'bg-amber-50 border-amber-200 text-amber-700',
  },
  {
    label: 'Target Server',
    icon: '🎯',
    color: 'bg-green-50 border-green-200 text-green-700',
  },
];

const FlowDiagram: FC = () => (
  <div className="flex items-center gap-0 py-3">
    {FLOW_STEPS.map((step, i) => (
      <Fragment key={step.label}>
        {i > 0 && (
          <div
            className="flex items-center px-0.5"
            style={{
              animation: `flowArrow 0.3s ease-out ${i * 0.15 + 0.1}s both`,
            }}
          >
            <svg
              width="24"
              height="12"
              viewBox="0 0 24 12"
              className="text-gray-300"
              aria-hidden="true"
              role="img"
            >
              <line
                x1="0"
                y1="6"
                x2="18"
                y2="6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeDasharray="3 2"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="10"
                  to="0"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </line>
              <polygon points="17,3 22,6 17,9" fill="currentColor" />
            </svg>
          </div>
        )}
        <div
          className={cn(
            'rounded-lg border px-3 py-1.5 text-[11px] font-medium flex items-center gap-1.5',
            step.color
          )}
          style={{
            animation: `fadeInUp 0.4s ease-out ${i * 0.15}s both`,
          }}
        >
          <span className="text-xs">{step.icon}</span>
          {step.label}
        </div>
      </Fragment>
    ))}
    <style>{`
      @keyframes fadeInUp {
        from { opacity: 0; transform: translateY(6px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes flowArrow {
        from { opacity: 0; transform: translateX(-4px); }
        to { opacity: 1; transform: translateX(0); }
      }
    `}</style>
  </div>
);

// ─── Compact Download Links ──────────────────────────────────────────────────

const CompactDownloadLinks: FC = () => {
  const samples = useSamples();
  if (samples.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5 text-xs text-gray-400">
      <span>Need a proxy?</span>
      {samples.map((sample, i) => (
        <Fragment key={sample.language}>
          {i > 0 && <span>·</span>}
          <button
            type="button"
            onClick={() => downloadSample(sample)}
            className="text-blue-600 hover:text-blue-800 font-medium transition-colors flex items-center gap-0.5"
          >
            <Download className="h-3 w-3" />
            {sample.filename}
          </button>
        </Fragment>
      ))}
    </div>
  );
};

// ─── Empty State Instructions ────────────────────────────────────────────────

const EmptyStateInstructions: FC<{
  onConfigure: () => void;
  showTitle?: boolean;
}> = ({ onConfigure, showTitle = true }) => {
  const samples = useSamples();

  return (
    <div className="space-y-4">
      {showTitle && (
        <h3 className="text-sm font-semibold text-gray-900">
          Send events to server
        </h3>
      )}
      <p className="text-xs text-gray-500 leading-relaxed">
        Send Chargebee event payloads to a local or remote server for testing
        webhook integrations. Events are forwarded through a lightweight proxy
        to avoid CORS issues.
      </p>

      {/* Follow these steps */}
      <div className="space-y-2">
        <h4 className="text-xs font-medium text-gray-700">
          Follow these steps
        </h4>
        <ol className="space-y-1.5 text-xs text-gray-600 list-decimal list-inside">
          <li>
            Download a proxy server sample below and run it on{' '}
            <code className="text-[11px] font-mono bg-gray-100 px-1 rounded">
              localhost:8000
            </code>
          </li>
          <li>
            Click{' '}
            <button
              type="button"
              onClick={onConfigure}
              className="font-medium text-blue-600 hover:text-blue-800 underline underline-offset-2 transition-colors"
            >
              Configure
            </button>{' '}
            to set your proxy URL{' '}
            <span className="text-gray-400">
              (e.g.{' '}
              <code className="text-[11px] font-mono bg-gray-100 px-0.5 rounded">
                http://localhost:8000
              </code>
              )
            </span>{' '}
            and target server URL{' '}
            <span className="text-gray-400">
              (where you want events delivered)
            </span>
          </li>
          <li>
            Select an event from the list and click{' '}
            <span className="font-medium">Forward Event</span> to send the
            payload through the proxy to your target server
          </li>
        </ol>
        <p className="text-[11px] text-gray-400 leading-relaxed">
          <span className="font-medium text-gray-500">Tip:</span> If your target
          server supports CORS, switch to{' '}
          <span className="font-medium">Via Browser</span> mode to skip the
          proxy and forward events directly from the browser.
        </p>
      </div>

      {/* Flow diagram */}
      <FlowDiagram />

      {/* Quick start — download proxy samples */}
      <div className="space-y-2">
        <h4 className="text-xs font-medium text-gray-700">
          Quick start — download a proxy server
        </h4>
        <div className="space-y-1.5">
          {samples.map((sample) => (
            <button
              type="button"
              key={sample.language}
              onClick={() => downloadSample(sample)}
              className="flex w-full items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-left hover:bg-blue-50 hover:border-blue-200 transition-colors cursor-pointer"
            >
              <span className="text-xs font-medium text-gray-700 w-12 shrink-0">
                {sample.label}
              </span>
              <span className="flex-1 text-[11px] text-blue-600 font-medium truncate">
                {sample.filename}
              </span>
              <Download className="h-3.5 w-3.5 text-gray-400 shrink-0" />
            </button>
          ))}
          <p className="text-[11px] text-gray-400 leading-relaxed pl-1">
            Basic Auth: include credentials in the target URL as{' '}
            <code className="font-mono bg-gray-100 px-1 rounded text-[10px]">
              https://user:password@host/path
            </code>
          </p>
        </div>
      </div>

      {/* Full-width CTA */}
      <Button
        onClick={onConfigure}
        className="w-full bg-shade-teal-600 text-white hover:bg-shade-teal-700"
      >
        <Settings className="h-4 w-4" />
        Configure Forwarding
      </Button>
    </div>
  );
};
